import settingsActionCreatorFactory from 'typescript-fsa'
import { Theme } from '@mui/material'
import { Navigation, SocialNetworkLicensePermissions, ActionCall, Languages } from '@professocial/professocialTypes/types/commonTypes'
import { PnrUser, UserLoginRequest, UserSettings } from '@professocialApi/professocial-core-frontend-api'
import {
  InitiateConnectOperationRequest, PerformConnectOperationRequest, PerformDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SmPublishConnectApi'
import {
  GetSmPublishAuthTokenResponse,
  InitiateConnectResponse, PageIdentifier, SocialmediaConnection, SocialmediaConnectResponse, UserLogin,
} from '@professocialApi/professocial-core-frontend-api'
import { FacebookConnectDataElement, GetFacebookConnectDataRequest, GetInstagramConnectDataRequest, InstagramConnectDataElement } from '@3m5/sm_publish-frontend-api'
import {
  SocialmediaUserConnectRequest,
  SocialmediaUserDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SocialmediaUserApi'
import {
  SocialmediaEditorConnectRequest,
  SocialmediaEditorDisconnectRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/SocialmediaEditorApi'
import {
  SetOrUpdateImprintRequest,
} from '@professocialApi/professocial-core-frontend-api/apis/ImprintApi'
import { Imprint } from '@professocialApi/professocial-core-frontend-api/models/Imprint'

import { Message } from '@professocial/professocialTypes/types/messageTypes'
import { UpdateUserAccountRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserLoginRESTServiceApi'
import { GetPageIdentifiersForUserRequest } from '@professocialApi/professocial-core-frontend-api/apis/UserHasPageApi'
import {
  AddUserPageRelationByAgencyNumberRequest,
  FindByPnrRequest
} from '@professocialApi/professocial-core-frontend-api/apis/UserSearchApi'
import { AnnouncementResponse } from '@professocialApi/professocial-core-frontend-api/models/AnnouncementResponse'
import { AnnouncementRequest } from '@professocialApi/professocial-core-frontend-api/models/AnnouncementRequest'
import { UpdateCurrentAnnouncementRequest } from '@professocialApi/professocial-core-frontend-api/apis/AnnouncementApi'

export const commonActionCreator = settingsActionCreatorFactory('SETTINGS')

/**
 * Actions
 */

// common actions
export const setFacebookConnectHash = commonActionCreator<string | undefined>('SET_FACEBOOK_CONNECT_HASH')
export const setInstagramConnectHash = commonActionCreator<string | undefined>('SET_INSTAGRAM_CONNECT_HASH')
export const setGoogleConnectHash = commonActionCreator<string | undefined>('SET_GOOGLE_CONNECT_HASH')
export const setConnectedFacebookPage = commonActionCreator<string>('SET_CONNECTED_FACEBOOK_PAGE')
export const setConnectedInstagramPage = commonActionCreator<string>('SET_CONNECTED_INSTAGRAM_PAGE')
export const setConnectedGooglePage = commonActionCreator<string>('SET_CONNECTED_GOOGLE_PAGE')
export const setConnectedLinkedInPage = commonActionCreator<string>('SET_CONNECTED_LINKED_IN_PAGE')
export const setInstagramNumberOfPost = commonActionCreator<24 | 9>('SET_INSTAGRAM_NUMBER_OF_POSTS')
export const setPageIdentifiersForUser = commonActionCreator<PageIdentifier[] | undefined>('SET_PAGE_IDENTIFIERS_FOR_USER')
export const setTokenChanged = commonActionCreator<boolean>('SET_TOKE_CHANGED')

export const setTheme = commonActionCreator<Theme>('SET_THEME')
export const setUser = commonActionCreator<UserSettings>('SET_USER')
export const setLicensePermissions = commonActionCreator<SocialNetworkLicensePermissions>('SET_SOCIAL_MEDIA_NETWORKS')
export const setNavigationList = commonActionCreator<Navigation[]>('SET_NAVIGATION_LIST')
export const setNavigationItem = commonActionCreator<string>('SET_NAVIGATION_ITEM')
export const setMessage = commonActionCreator<Message>('SET_MESSAGE')
export const setDevelopmentSystem = commonActionCreator<boolean>('SET_DEVELOPMENT_SYSTEM')
export const setLanguage = commonActionCreator<Languages[]>('SET_LANGUAGES')

export const requestStarted = commonActionCreator<ActionCall>('REQUEST_STARTED')
export const requestFinished = commonActionCreator<ActionCall>('REQUEST_FINISHED')
export const requestFailed = commonActionCreator<ActionCall>('REQUEST_FAILED')
export const setPnrUser = commonActionCreator<UserLogin>('SET_PNR_USER')

export const userLoginInitiated = commonActionCreator<UserLoginRequest>('USER_LOGIN_INITIATED')
export const userLogin = commonActionCreator.async<UserLoginRequest, UserSettings>('USER_LOGIN')

export const logoutInitiated = commonActionCreator('LOGOUT_INITIATED')
export const logout = commonActionCreator.async<undefined, undefined>('LOGOUT')

export const getCurrentUserInitiated = commonActionCreator('GET_CURRENT_USER_INITIATED')
export const getCurrentUser = commonActionCreator.async<undefined, UserSettings>('GET_CURRENT_USER')

// Schritt 0 > Core > getToken
export const getSmPublishAuthTokenInitiated = commonActionCreator('GET_SM_PUBLISH_AUTH_TOKEN_INITIATED')
export const getSmPublishAuthToken = commonActionCreator.async<undefined, GetSmPublishAuthTokenResponse>('GET_SM_PUBLISH_AUTH_TOKEN')

// Schritt 1 > OAUTH > connect / disconnect
// USER
export const socialmediaUserConnectInitiated = commonActionCreator<SocialmediaUserConnectRequest>('SOCIALMEDIA_USER_CONNECT_INITIATED')
export const socialmediaUserConnect = commonActionCreator.async<SocialmediaUserConnectRequest, SocialmediaConnectResponse>('SOCIALMEDIA_USER_CONNECT')

export const socialmediaUserDisconnectInitiated = commonActionCreator<SocialmediaUserDisconnectRequest>('SOCIALMEDIA_USER_DISCONNECT_INITIATED')
export const socialmediaUserDisconnect = commonActionCreator.async<SocialmediaUserDisconnectRequest, SocialmediaConnection>('SOCIALMEDIA_USER_DISCONNECT')

// EDITOR
export const socialmediaEditorConnectInitiated = commonActionCreator<SocialmediaEditorConnectRequest>('SOCIALMEDIA_EDITOR_CONNECT_INITIATED')
export const socialmediaEditorConnect = commonActionCreator.async<SocialmediaEditorConnectRequest, SocialmediaConnectResponse>('SOCIALMEDIA_EDITOR_CONNECT')

export const socialmediaEditorDisconnectInitiated = commonActionCreator<SocialmediaEditorDisconnectRequest>('SOCIALMEDIA_EDITOR_DISCONNECT_INITIATED')
export const socialmediaEditorDisconnect = commonActionCreator.async<SocialmediaEditorDisconnectRequest, SocialmediaConnection>('SOCIALMEDIA_EDITOR_DISCONNECT')

// Schritt 2 > CORE
export const initiateFacebookConnectInitiated = commonActionCreator<InitiateConnectOperationRequest>('INITIATE_FACEBOOK_CONNECT_INITIATED')
export const initiateFacebookConnect = commonActionCreator.async<InitiateConnectOperationRequest, InitiateConnectResponse>('INITIATE_FACEBOOK_CONNECT')

export const getPageIdentifierForCurrentUserInitiated = commonActionCreator<void>('GET_PAGES_FOR_CURRENT_USER_INITIATED')
export const getPageIdentifierForCurrentUser = commonActionCreator.async<void, PageIdentifier[]>('GET_PAGES_FOR_CURRENT_USER')

export const initiateInstagramConnectInitiated = commonActionCreator<InitiateConnectOperationRequest>('INITIATE_INSTAGRAM_CONNECT_INITIATED')
export const initiateInstagramConnect = commonActionCreator.async<InitiateConnectOperationRequest, InitiateConnectResponse>('INITIATE_INSTAGRAM_CONNECT')

export const initiateGoogleConnectInitiated = commonActionCreator<InitiateConnectOperationRequest>('INITIATE_GOOGLE_CONNECT_INITIATED')
export const initiateGoogleConnect = commonActionCreator.async<InitiateConnectOperationRequest, InitiateConnectResponse>('INITIATE_GOOGLE_CONNECT')

// Schritt 3 > SM_PUBLISH
export const getFacebookConnectDataInitiated = commonActionCreator<GetFacebookConnectDataRequest>('GET_FACEBOOK_CONNECT_DATA_INITIATED')
export const getFacebookConnectData = commonActionCreator.async<GetFacebookConnectDataRequest, FacebookConnectDataElement[]>('GET_FACEBOOK_CONNECT_DATA')

export const getInstagramConnectDataInitiated = commonActionCreator<GetInstagramConnectDataRequest>('GET_INSTAGRAM_CONNECT_DATA_INITIATED')
export const getInstagramConnectData = commonActionCreator.async<GetInstagramConnectDataRequest, InstagramConnectDataElement[]>('GET_INSTAGRAM_CONNECT_DATA')

// Schritt 4 > CORE
export const performConnectInitiated = commonActionCreator<PerformConnectOperationRequest>('PERFORM_CONNECT_INITIATED')
export const performConnect = commonActionCreator.async<PerformConnectOperationRequest, undefined>('PERFORM_CONNECT')

export const performDisconnectInitiated = commonActionCreator<PerformDisconnectRequest>('PERFORM_DISCONNECT_INITIATED')
export const performDisconnect = commonActionCreator.async<PerformDisconnectRequest, undefined>('PERFORM_DISCONNECT')

// User
export const updateUserAccountInitiated = commonActionCreator<UpdateUserAccountRequest>('UPDATE_USER_ACCOUNT_INITIATED')
export const updateUserAccount = commonActionCreator.async<UpdateUserAccountRequest, UserLogin>('UPDATE_USER_ACCOUNT')

// Imprint
export const getPotentialPageImprintInitiated = commonActionCreator('GET_POTENTIAL_PAGE_IMPRINT_INITIATED')
export const getPotentialPageImprint = commonActionCreator.async<undefined, Imprint>('GET_POTENTIAL_PAGE_IMPRINT')

export const setOrUpdateImprintInitiated = commonActionCreator<SetOrUpdateImprintRequest>('SET_OR_UPGRADE_IMPRINT_INITIATED')
export const setOrUpdateImprint = commonActionCreator.async<SetOrUpdateImprintRequest, Imprint>('SET_OR_UPGRADE_IMPRINT')

export const getPageIdentifiersForUserInitiated = commonActionCreator<GetPageIdentifiersForUserRequest>('GET_PAGE_IDENTIFIERS_FOR_USER_INITIATED')
export const getPageIdentifiersForUser = commonActionCreator.async<GetPageIdentifiersForUserRequest, PageIdentifier[]>('GET_PAGE_IDENTIFIERS_FOR_USER')

export const getUserByPnrInitiated = commonActionCreator<FindByPnrRequest>('GET_USER_BY_PNR_INITIATED')
export const getUserByPnr = commonActionCreator.async<FindByPnrRequest, PnrUser>('GET_USER_BY_PNR')
export const resetPnrUser = commonActionCreator('RESET_PNR_USER')

export const addUserPageRelationByAgencyNumberInitiated = commonActionCreator<AddUserPageRelationByAgencyNumberRequest>('ADD_USER_PAGE_RELATION_BY_AGENCY_NUMBER_INITIATED')
export const addUserPageRelationByAgencyNumber = commonActionCreator.async<AddUserPageRelationByAgencyNumberRequest, undefined>('ADD_USER_PAGE_RELATION_BY_AGENCY_NUMBER')

// == Announcements ==

export const getActiveAnnouncementInitiated = commonActionCreator('GET_ACTIVE_ANNOUNCEMENT_INITIATED')
export const getActiveAnnouncement = commonActionCreator.async<undefined, AnnouncementResponse>('GET_ACTIVE_ANNOUNCEMENT')

export const getAnnouncementTemplateInitiated = commonActionCreator('GET_ANNOUNCEMENT_TEMPLATE_INITIATED')

export const getAnnouncementTemplate = commonActionCreator.async<AnnouncementRequest, AnnouncementResponse>('GET_ANNOUNCEMENT_TEMPLATE')

export const updateAnnouncementInitiated = commonActionCreator<UpdateCurrentAnnouncementRequest>('UPDATE_ANNOUNCEMENT_INITIATED')

export const updateAnnouncement = commonActionCreator.async<UpdateCurrentAnnouncementRequest, AnnouncementResponse>('UPDATE_ANNOUNCEMENT')

export const acceptAnnouncementInitiated = commonActionCreator('ACCEPT_ANNOUNCEMENT_INITIATED')

export const acceptAnnouncement = commonActionCreator.async('ACCEPT_UPDATE_ANNOUNCEMENT')
